// OverflowProvider.tsx
import MoreVertIcon from '@mui/icons-material/MoreVertRounded';
import React, { ReactNode } from 'react';
import DefaultAppBarButton from '../Buttons/DefaultAppBarButton/styles';
import { OverflowContext, OverflowItemData } from './OverflowContext';
import StyledMenu from './OverflowMenu';
import computeVisibleElements from './utils/computeVisibleElements';

export default function OverflowProvider({ children }: { children: ReactNode }) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [itemsMap, setItemsMap] = React.useState<Record<string, { position: 'left' | 'right' | 'middle', priority: number; width: number; ref: HTMLElement | null }>>({});
  const [itemMenuComponentsMap, setItemMenuComponentsMap] = React.useState<Record<string, React.ReactNode>>({});
  const [containerWidth, setContainerWidth] = React.useState(0);
  const [visibleIds, setVisibleIds] = React.useState<string[]>([]);
  const [overflowIds, setOverflowIds] = React.useState<string[]>([]);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<HTMLElement | null>(null);

  const registerItemMenuComponent = React.useCallback((id: string, menuComponent: React.ReactNode) => {
    setItemMenuComponentsMap((prev) => ({
      ...prev,
      [id]: menuComponent,
    }));
  }, []);

  const MenuComponents = React.useMemo(
    () => overflowIds
      .map((id) => {
        const component = itemMenuComponentsMap[id];
        if (!component) return null; // Skip if no component registered for this id

        // Clone the single element to guarantee it has a `key={id}`
        return React.isValidElement(component)
          ? React.cloneElement(component, { key: id })
          : null;
      })
      .filter(Boolean),
    [overflowIds, itemMenuComponentsMap],
  );

  /**
   * registerItem: called by each <OverflowItem> when it mounts/updates
   * - id: unique string
   * - priority: number (lower = higher priority)
   * - ref: the actual HTMLElement so we can measure it
   */
  const registerItem = React.useCallback(
    (id: string, position: 'left' | 'right' | 'middle', priority: number, el: HTMLElement | null, menuComponent?: React.ReactNode) => {
      setItemsMap((prev) => {
        // If width is known, keep it; otherwise measure now if el exists.
        let width = prev[id]?.width ?? 0;
        if (el) {
          const rect = el.getBoundingClientRect();
          width = rect.width;
        }
        return {
          ...prev,
          [id]: {
            priority,
            width,
            position,
            ref: el,
            menuComponent,
          },
        };
      });
    },
    [],
  );

  /** Called by <OverflowItem> when it unmounts */
  const unregisterItem = React.useCallback((id: string) => {
    setItemsMap((prev) => {
      if (!(id in prev)) return prev;
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  }, []);

  React.useEffect(() => {
    if (!containerRef.current) return undefined;
    const ro = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const w = entry.contentRect.width;
        setContainerWidth(w);
      });
    });
    ro.observe(containerRef.current);
    return () => {
      ro.disconnect();
    };
  }, [containerRef.current]);

  // Whenever itemsMap or containerWidth changes, recompute visible/overflow
  React.useEffect(() => {
    const entries: OverflowItemData[] = Object.entries(itemsMap).map(
      ([id, { priority, width, position }]) => ({
        id, priority, width, position,
      }),
    );

    const withVisibility = computeVisibleElements(entries, containerWidth);
    const { visible, overflow } = withVisibility.reduce(
      (acc, item) => {
        if (item.visible) {
          acc.visible.push(item.id);
        } else {
          acc.overflow.push(item.id);
        }
        return acc;
      },
      { visible: [] as string[], overflow: [] as string[] },
    );

    setVisibleIds(visible);
    setOverflowIds(overflow);
  }, [itemsMap, containerWidth]);

  // Expose context value
  const ctxValue = React.useMemo(() => ({
    registerItem,
    unregisterItem,
    registerItemMenuComponent,
    containerRef,
    isVisible: (id: string) => visibleIds.includes(id),
    showMoreButton: MenuComponents.length > 0,
    setAnchorEl: setMenuAnchorEl,
  }), [
    registerItem,
    unregisterItem,
    registerItemMenuComponent,
    containerRef,
    visibleIds,
    MenuComponents.length > 0,
  ]);

  return (
    <OverflowContext.Provider value={ctxValue}>
      {/* Render everything inside here */}
      {children}
      {/*
        Place a hidden, off-screen dummy button with id="__overflow-button_dummy"
        so we can measure how wide “⋯” is (once).
      */}
      <DefaultAppBarButton
        id="__overflow-button_dummy"
        style={{
          visibility: 'hidden',
          position: 'absolute',
          height: 0,
          overflow: 'hidden',
        }}
      >
        <MoreVertIcon />
      </DefaultAppBarButton>
      <StyledMenu
        id="overflow-menu"
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
        MenuListProps={{
          'aria-labelledby': 'overflow-button',
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        {MenuComponents}
      </StyledMenu>
    </OverflowContext.Provider>
  );
}
